import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useSSRComponent } from '../hooks/use-ssr-component';

export type SsrComponentProps = {
  route: string;
  txaId: string;
  fallback: React.ReactNode;
};

type LeftSideBarSpecs = {
  leftSidebarExpandedWidth?: string;
  leftSidebarCollapsedWidth?: string;
};

const attachLinkHandler = (container: HTMLElement, history: any) => {
  const handleClick = (event: MouseEvent) => {
    const link = (event.target as HTMLElement).closest('a');
    if (link && !link.hasAttribute('data-should-reload')) {
      event.preventDefault();
      const href = link.getAttribute('href');
      if (href) {
        history.push(href);
      }
    }
  };
  container.addEventListener('click', handleClick);
};

const attachDropdownHandlers = (container: HTMLElement) => {
  const dropdownButton = container.querySelector('[data-testid*="ssr-drop-down-btn"]');
  const dropdownContent = container.querySelector('[data-testid*="ssr-drop-down-menu-group"]')
    ?.parentNode as HTMLElement;

  const toggleDropdown = () => {
    dropdownContent.classList.toggle('hide');
    dropdownContent.classList.toggle('show');
  };
  if (dropdownButton && dropdownContent) {
    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownButton.contains(event.target as Node) && !dropdownContent.contains(event.target as Node)) {
        dropdownContent.classList.add('hide');
        dropdownContent.classList.remove('show');
      }
    };
    if (dropdownButton) {
      dropdownButton.addEventListener('click', toggleDropdown);
      document.addEventListener('click', handleClickOutside);
    }
  }
};

const attachNavItemHandlers = (container: HTMLElement) => {
  const navLinks = container.querySelectorAll('.nav-items a');

  navLinks.forEach((link) => {
    link.addEventListener('mousedown', function (this: HTMLElement) {
      container.querySelectorAll('.nav-items a').forEach((otherLink) => {
        otherLink.classList.remove('nav-active-item');
      });

      this.classList.add('nav-active-item');
    });
  });
};

const getCSSVariable = (element: HTMLElement, variableName: string): string => {
  return getComputedStyle(element).getPropertyValue(variableName).trim();
};

const toggleCSSVariable = (variableName: string, defaultValue: string, alternateValue: string): void => {
  const currentValue = document.documentElement.style.getPropertyValue(variableName).trim();
  document.documentElement.style.setProperty(
    variableName,
    currentValue === defaultValue || currentValue === '' ? alternateValue : defaultValue
  );
};

const attachResizeHandler = (
  container: HTMLElement,
  { leftSidebarExpandedWidth = '', leftSidebarCollapsedWidth = '' }: LeftSideBarSpecs
) => {
  const changeWidthBtn = container.querySelector('[data-testid*="resize-button"]');
  if (changeWidthBtn) {
    const handleClick = () => {
      toggleCSSVariable('--leftSidebarWidth', leftSidebarExpandedWidth, leftSidebarCollapsedWidth);
      changeWidthBtn.querySelector('svg')?.classList.toggle('resize-btn-expand');
    };

    changeWidthBtn.addEventListener('click', handleClick);
  }
};

export const SsrComponent = ({ route, txaId, fallback }: SsrComponentProps) => {
  const { ssrHtml, error } = useSSRComponent(`${route}?txaId=${txaId} `);
  const containerRef: any = useRef(null);
  const history: any = useHistory();
  const [leftSideBarSpecs, setLeftSideBarSpecs] = useState<LeftSideBarSpecs | null>(null);

  useEffect(() => {
    const container = containerRef.current as HTMLElement;
    if (ssrHtml && container) {
      const leftSidebarContainer = container.querySelector('[data-testid="left-sidebar-container"]');
      if (leftSidebarContainer) {
        const leftSidebarExpandedWidth = getCSSVariable(
          leftSidebarContainer as HTMLElement,
          '--leftSidebarExpandedWidth'
        );
        const leftSidebarCollapsedWidth = getCSSVariable(
          leftSidebarContainer as HTMLElement,
          '--leftSidebarCollapsedWidth'
        );
        setLeftSideBarSpecs({ leftSidebarExpandedWidth, leftSidebarCollapsedWidth });
      }
      attachLinkHandler(container, history);
      attachDropdownHandlers(container);
      attachNavItemHandlers(container);
    }
  }, [ssrHtml, containerRef]);

  useEffect(() => {
    const container = containerRef.current as HTMLElement;
    if (leftSideBarSpecs) {
      attachResizeHandler(container, leftSideBarSpecs);
    }
  }, [leftSideBarSpecs]);

  if (!ssrHtml && !error) {
    return <div>{'Loading...'}</div>;
  }
  if (error) {
    return <>{fallback}</>;
  }

  return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ssrHtml) }} />;
};
